import React, { useContext, useEffect, useState } from 'react';
import { AppContext, OrderDeletionContext } from '../../App';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteOrder,
  getOrderDetails,
  getOrderDetailsLazy,
} from '../../clients/order_client';
import OrderModal from './OrderModal';
import { ORDER_HARD_DELETE_OPTION } from './orderHardDeleteOption';
import QuoteDetailModal from '../Quote/QuoteDetailModal';
import { getQuoteDetailsLazy } from '../../clients/quote_client';
import CarouselComponent from '../Generic/CarouselComponent';
import OrderProductLabel from './OrderProductLabel';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import S3MediaLink from '../Generic/S3MediaLink';
import {
  formatDateStr,
  getAssociatedUserDisplay,
  resolveEntityId,
} from '../../config_and_helpers/helpers';
import { logError } from '../../config_and_helpers/logger';
import OrderFundingDisplay from './OrderFundingDisplay';
import PrintLabelsModal from '../printing/PrintLabelsModal';
import {
  enrichOrderWithQuoteFunding,
  mergeOrderDetailsForDisplay,
  needsOrderFundingEnrichment,
} from '../../utils/quoteFundingDisplay';

/**
 * Represents a modal component for displaying order details.
 * @param {Object} fetchedOrdersRef - The reference to the fetched orders.
 * @param {Function} setFetchedOrders - The function to set the fetched orders.
 * @param {Object} orderObj - The order object to display.
 * @param {Function} updateOrders - The function to update the orders.
 * @param {string} orderId - The ID of the order.
 * @param {Function} clearSearchValue - The function to clear the search value.
 * @param {boolean} [hideTrigger=false] - Hide the link button (use with controlled `show`).
 * @param {boolean} [show] - Controlled modal visibility.
 * @param {function} [onHide] - Called when controlled modal closes.
 * @returns {JSX.Element} The order modal component.
 */
const OrderDetailModal = ({
  fetchedOrdersRef,
  setFetchedOrders,
  orderObj,
  updateOrders,
  orderId,
  clearSearchValue,
  hideTrigger = false,
  show: controlledShow,
  onHide: controlledOnHide,
}) => {
  // Hooks for necessary states and context values
  const { token } = useContext(AppContext);
  const { toggleOrderDeleted } = useContext(OrderDeletionContext);

  // Provide a mutable value that exists for the whole lifetime of the component
  const [loadingOrder, setLoadingOrder] = useState(false);
  // State for controlling the showing of the modal (uncontrolled unless `show` is passed).
  const [internalShow, setInternalShow] = useState(false);
  const isControlled = controlledShow !== undefined;
  const show = isControlled ? controlledShow : internalShow;
  // State for manging the Order object
  const [order, setOrder] = useState(orderObj);

  // Lazy loading states
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  // Resolve id from prop or loaded order (QuoteTable drops orderId after hover-fetch)
  const orderIdToUse = resolveEntityId(
    orderId ?? order?.id ?? orderObj?.id
  );

  // UseEffect to update the state on re-renders
  useEffect(() => {
    setOrder(orderObj);
  }, [orderObj]);

  // Boolean to determine if the orderId already exists in the fetched orders Ref
  const isOrderInFetchedOrders = fetchedOrdersRef
    ? fetchedOrdersRef.current.has(orderIdToUse)
    : false;

  // Function to fetch detailed order data with lazy loading
  const fetchDetailedData = async () => {
    if (!orderIdToUse || detailedData || loadingDetails) {
      return; // Already loaded, loading, or no orderId
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const data = await getOrderDetailsLazy(token, orderIdToUse);
      const listOrder = order ?? orderObj;
      let merged = mergeOrderDetailsForDisplay(listOrder, data);

      if (needsOrderFundingEnrichment(merged)) {
        const quoteId = resolveEntityId(merged?.quote);
        if (quoteId) {
          const quoteDetails = await getQuoteDetailsLazy(token, quoteId);
          merged = enrichOrderWithQuoteFunding(merged, quoteDetails);
        }
      }

      setDetailedData(merged);
    } catch (err) {
      logError('OrderDetailModal', 'Error fetching details', err);
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fetches specific order data
  const fetchOrder = () => {
    if (!orderIdToUse || isOrderInFetchedOrders) {
      return;
    }

    // If rendered via a table (thus passing in the ref), add the order id to the fetched orders ref
    if (fetchedOrdersRef) fetchedOrdersRef.current.add(orderIdToUse);

    setLoadingOrder(true);
    getOrderDetails(token, orderIdToUse, setOrder).then(response => {
      if (response.success && setFetchedOrders) {
        // If fetched, and rendered via a table (thus passing in the setFetchedOrders function), update the fetched supplier state
        setFetchedOrders(prevFetchedOrders => [
          ...prevFetchedOrders,
          response.orderData,
        ]);
      }
    }).finally(() => setLoadingOrder(false));
  };

  // Remove automatic fetching - will only fetch on hover

  // Callback for when an order is deleted
  const onOrderDelete = () => {
    toggleOrderDeleted();
    if (typeof updateOrders === 'function') {
      updateOrders();
    }
    setDetailedData(null);
    handleClose();
  };

  // Handlers for opening and closing the modal
  const handleClose = () => {
    if (isControlled) {
      controlledOnHide?.();
      return;
    }
    setInternalShow(false);
  };
  const handleShow = () => {
    if (!isControlled) {
      setInternalShow(true);
    }
    if (!order && orderIdToUse) {
      fetchOrder();
    }
    fetchDetailedData();
  };

  // Controlled open (e.g. AI chat citation table): fetch immediately.
  useEffect(() => {
    if (!hideTrigger || !show || !orderIdToUse) return;
    if (!order) {
      fetchOrder();
    }
    fetchDetailedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideTrigger, show, orderIdToUse]);

  // Load details when id becomes available after hover-fetch (QuoteTable drops orderId prop)
  useEffect(() => {
    if (show && orderIdToUse && !detailedData && !loadingDetails && !error) {
      fetchDetailedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderIdToUse]);

  // Note: Removed early spinner return to allow modal to show with loading state inside

  return (
    <div>
      {/* Always render the button, even when order data is not yet loaded */}
      {!hideTrigger && (
        <Button
          variant="link"
          onClick={handleShow}
          onMouseEnter={() => {
            // Fetch order data on hover if orderId is provided and not already fetched
            if (orderIdToUse && !isOrderInFetchedOrders) {
              fetchOrder();
            }
            // No need to fetch quote details - order object has quote status
          }}
        >
          {order ? `Order ${order.id}` : `Order ${orderIdToUse ?? '?'}`}
        </Button>
      )}

      {hideTrigger && show && !order && (
        <Modal show onHide={handleClose} centered>
          <Modal.Body className="d-flex align-items-center justify-content-center gap-2 py-4">
            <Spinner animation="border" size="sm" role="status" aria-label="Loading order" />
            <span>Loading order…</span>
          </Modal.Body>
        </Modal>
      )}

      {/* Only render the modal if order data is available */}
      {order && (
        <Modal
          show={show}
          onHide={handleClose}
          aria-labelledby="product-modal"
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Order {order.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Loading state for order data */}
            {loadingOrder && (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading order...</span>
                </Spinner>
                <p className="mt-2">Loading order data...</p>
              </div>
            )}
            {order && !loadingOrder && (
              <Container>
                {/* Loading state for detailed data */}
                {loadingDetails && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading order details...</p>
                  </div>
                )}

                {/* Error state */}
                {error && (
                  <div className="alert alert-danger mb-3">
                    <p className="mb-2">Error loading order details: {error}</p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={fetchDetailedData}
                    >
                      Retry
                    </Button>
                  </div>
                )}

                {/* Display order details using detailed data when available */}
                {detailedData && (
                  <>
                    {/* The Container, Row, and Col components are from react-bootstrap and used for layout */}
                    {/* Various pieces of information about the order are listed here */}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Quote:</p>
                      </Col>
                      <Col className="d-flex flex-row align-items-center me-3">
                        {detailedData.quote && (
                          <>
                            <QuoteDetailModal quoteId={detailedData.quote.id} />
                            <span className="ms-2 me-3">{'|'}</span>
                            <S3MediaLink
                              url={detailedData.quote.quote_url}
                              quoteId={detailedData.quote.id}
                              title="Open quote PDF"
                              className="link-"
                            >
                              <PictureAsPdfIcon />
                            </S3MediaLink>
                          </>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Reception Dates:</p>
                      </Col>
                      <Col>
                        <div className="fs-6">
                          {detailedData.reception_dates &&
                          detailedData.reception_dates.length > 0 ? (
                            detailedData.reception_dates.map((date, index) => (
                              <div key={index}>
                                {formatDateStr(date)}
                                {index <
                                  detailedData.reception_dates.length - 1 && (
                                  <br />
                                )}
                              </div>
                            ))
                          ) : (
                            <span className="text-muted">
                              No reception dates
                            </span>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Received By:</p>
                      </Col>
                      <Col>
                        <p className="fs-6">{detailedData.received_by}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Supplier: </p>
                      </Col>
                      <Col>
                        <SupplierDetailModal
                          supplierObj={detailedData.supplier}
                          supplierId={detailedData.supplier.id}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Status: </p>
                      </Col>
                      <Col>
                        <p className="fs-6">{detailedData.quote.status}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Order Reference: </p>
                      </Col>
                      <Col>
                        <p className="fs-6">
                          {detailedData.corporate_order_ref}
                        </p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Funding: </p>
                      </Col>
                      <Col>
                        <OrderFundingDisplay order={detailedData} />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Associated User: </p>
                      </Col>
                      <Col>
                        {getAssociatedUserDisplay(detailedData) ? (
                          <p className="fs-6">
                            {getAssociatedUserDisplay(detailedData)}
                            {detailedData.quote?.user && !detailedData.user && (
                              <span className="text-muted ms-2">
                                (from quote)
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="fs-6 text-muted">No user associated</p>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Quote Items: </p>
                      </Col>
                    </Row>
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData.items?.map((item, index) => {
                          return (
                            <React.Fragment key={index}>
                              <tr className="text-center italic-text">
                                <td>{index + 1}</td>
                                <td key={index}>
                                  <OrderProductLabel
                                    product={item.product}
                                    productId={item.product?.id}
                                    isSupplier={false}
                                    updateProducts={() => {}}
                                    clearSearchValue={() => {}}
                                    handleProductUpdateFromSupplierTable={() => {}}
                                    inventoryModalButtonLargeFontSize={false}
                                  />
                                </td>
                                <td>
                                  {(() => {
                                    const received =
                                      item.lot_items && item.lot_items.length > 0
                                        ? item.lot_items.reduce(
                                            (sum, lot) =>
                                              sum +
                                              (lot.lotItemQuantity ||
                                                lot.lot_item_quantity ||
                                                0),
                                            0
                                          )
                                        : item.quantity || 0;
                                    const ordered = item.quote_item?.quantity;
                                    return ordered != null
                                      ? `${received} / ${ordered}`
                                      : received;
                                  })()}
                                </td>
                                <td>{item.status}</td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>

                    {(() => {
                      const orderFiles = detailedData.order_files || {
                        po_files: [],
                        receipt_files: [],
                      };
                      const po = orderFiles.po_files || [];
                      const receipts = orderFiles.receipt_files || [];
                      return (
                        <>
                          {po.length > 0 && (
                            <div className="mt-3">
                              <h6>Purchase order (PO)</h6>
                              <CarouselComponent
                                images={po}
                                imageIdKind="orderImageId"
                                orderFileCategory="po"
                              />
                            </div>
                          )}
                          {receipts.length > 0 && (
                            <div className="mt-3">
                              <h6>Receipts / invoices</h6>
                              <CarouselComponent
                                images={receipts}
                                imageIdKind="orderImageId"
                                orderFileCategory="receipt"
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </Container>
            )}{' '}
            {/* closes {order && !loadingOrder && ( */}
          </Modal.Body>

          {/* The footer contains buttons that interact with the order */}
          {order && (
            <Modal.Footer className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-row">
                {detailedData && (
                  <div className="me-2">
                    <PrintLabelsModal orderId={detailedData.id} />
                  </div>
                )}

                {/* Button to show a modal for editing the order. Triggers handleEdit when the edits are successful */}
                {detailedData && (
                  <div className="me-2">
                    <OrderModal
                      orderObj={detailedData}
                      clearSearchValue={clearSearchValue}
                      onOrderUpdated={() => {
                        if (typeof updateOrders === 'function') {
                          updateOrders();
                        }
                        setDetailedData(null);
                        setLoadingDetails(true);
                        setError(null);
                        getOrderDetailsLazy(token, orderIdToUse)
                          .then(data => setDetailedData(data))
                          .catch(err =>
                            setError(
                              err.message || 'Failed to reload order details'
                            )
                          )
                          .finally(() => setLoadingDetails(false));
                      }}
                    />
                  </div>
                )}

                {/* Button to delete the order. The deleteFetchFunc prop prescribes a function from order_client.js to make the API call */}
                {detailedData && (
                  <DeleteButton
                    objectType="order"
                    objectName={detailedData.id}
                    objectId={detailedData.id}
                    deleteFetchFunc={deleteOrder}
                    onSuccessfulDelete={onOrderDelete} // Triggers the provided onDelete function when the delete operation is successful
                    clearSearchValue={clearSearchValue}
                    disableDelete={
                      detailedData.can_delete_prepayment_order === false
                    }
                    disabledTooltipReason={
                      detailedData.can_delete_prepayment_order === false
                        ? detailedData.cannot_delete_prepayment_order_reason ??
                          'This prepayment order cannot be deleted because its supplier credit has already been used on other orders.'
                        : undefined
                    }
                    hardDeleteOption={ORDER_HARD_DELETE_OPTION}
                  />
                )}
              </div>

              {/* Button to close the modal */}
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </div>
  );
};
export default OrderDetailModal;
